
import React from 'react';
import Navbar from '@/components/navbar';
import Footer from '@/components/Footer';
import { Separator } from '@/components/ui/separator';

const CookiePolicy: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-20">
        <div className="container px-4 mx-auto">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-8">Cookie Policy</h1>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">1. Introduction & Scope</h2>
              <p className="text-muted-foreground mb-4">
                Dropify uses cookies and similar tracking technologies to enhance your experience. 
                This Cookie Policy explains what cookies are, how we use them, and how you can control their usage.
              </p>
              <p className="text-muted-foreground mb-4">
                By using Dropify, you agree to our use of cookies as outlined in this policy.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">2. What Are Cookies?</h2>
              <p className="text-muted-foreground mb-4">
                Cookies are small text files stored on your device that help websites remember information about your visit. 
                They can be session cookies (which expire after your session) or persistent cookies 
                (which stay on your device until deleted).
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">3. How We Use Cookies</h2>
              <p className="text-muted-foreground mb-2">We use cookies to:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span className="text-green-500">✅</span> Improve website functionality and performance
                </li>
                <li>
                  <span className="text-green-500">✅</span> Keep you logged in securely
                </li>
                <li>
                  <span className="text-green-500">✅</span> Track website analytics and user behavior
                </li>
                <li>
                  <span className="text-green-500">✅</span> Enable seamless Spotify & Stripe integration
                </li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Dropify does not use cookies for advertising or third-party tracking.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">4. Types of Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse mb-4">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left">Type</th>
                      <th className="py-2 px-3 text-left">Purpose</th>
                      <th className="py-2 px-3 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50">
                      <td className="py-2 px-3 font-medium">Essential Cookies</td>
                      <td className="py-2 px-3">Required for core platform functionality (e.g., authentication, security)</td>
                      <td className="py-2 px-3">Session/Persistent</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 px-3 font-medium">Analytics Cookies</td>
                      <td className="py-2 px-3">Help us track usage patterns and improve the platform (e.g., Google Analytics)</td>
                      <td className="py-2 px-3">Persistent</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 px-3 font-medium">Preference Cookies</td>
                      <td className="py-2 px-3">Store user preferences (e.g., dark mode setting)</td>
                      <td className="py-2 px-3">Persistent</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">5. Managing Cookies & Opt-Out Options</h2>
              <p className="text-muted-foreground mb-2">You can control or delete cookies in the following ways:</p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground mb-4">
                <li>
                  <span className="text-blue-500">🔹</span> <span className="font-medium">Browser Settings:</span> You can disable cookies via your browser settings, but some features may not work.
                </li>
                <li>
                  <span className="text-blue-500">🔹</span> <span className="font-medium">Cookie Banner:</span> Upon first visit, you can choose to accept or reject non-essential cookies.
                </li>
                <li>
                  <span className="text-blue-500">🔹</span> <span className="font-medium">Third-Party Tools:</span> Some analytics cookies (e.g., Google Analytics) can be disabled via Google's opt-out tool.
                </li>
              </ul>
              <p className="text-amber-500 font-medium mb-4">
                📌 Note: Disabling essential cookies may affect site functionality.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">6. Changes to This Policy</h2>
              <p className="text-muted-foreground mb-4">
                We may update this Cookie Policy periodically. If significant changes occur, 
                we will notify users through the website.
              </p>
            </section>
            
            <Separator className="my-6" />
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-3">7. Contact Information</h2>
              <p className="text-muted-foreground mb-4">
                For any questions about our Cookie Policy, contact:<br />
                <span className="text-primary">📧 support@dropify.com</span>
              </p>
            </section>
            
            <div className="mt-10 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CookiePolicy;
